// Draaidagen voor de planningsschermen (Rooster en Capaciteit).
//
// Joris (25 sep): "ik zie niet wanneer iemand een draaidag heeft". Capaciteit kende alleen
// taken met Type=Draaidag (die op test niet bestaan) en het Rooster alleen de grijze
// agendavulling. De bron is de draaidagen-tabel zelf.
//
// Bron:
// - GET /Api/draaidagen?startDate=…&endDate=…  (engine sinds sep 2026) elke actieve draaidag
//   die het venster raakt, met de pure datums (StartDatum/EindDatum) én de crew compleet:
//   fields["Email (from Crew)"], fields.Crew (persoon-record-id's) en fields.CrewLeden
//   ([{id, naam, email}]). Eén route, geen tweede bron nodig.
// - Terugval (oude engine, bv. test vóór de deploy): die kent endDate niet en levert crew
//   alleen als voornaam. Dan vullen we de e-mails aan uit GET /Api/draaidagen/upcoming
//   (alleen ná vandaag, datums uit StartdatumOutlook = UTC) — gekoppeld op record-id,
//   nooit voor de datums.

// Regels (CLAUDE.md, "Draaidagen: 4-laags filter + naam-fallback"):
// - is_valid !== false, all_day !== false, hidden !== true, outlook_link_status !== false
// - naam = projectnaam || draaidagnaam (zonder crew/opties) || 'Draaidag'
// - crew: e-mail → persoon, dan record-id, voornaam alleen als laatste redmiddel
// - 'OPTIE'/'Optie' in de naam = optie
// - datums = StartDatum/EindDatum (pure datums)

import { BACKEND_URL } from './apiConfig';
import { authFetch } from './authHelpers';
import { leesJson } from './apiFout';
import { haal, leesCache, planningSleutel, MINUUT } from './planningCache';

const EP_DRAAIDAGEN = '/Api/draaidagen';
const EP_AANKOMEND = '/Api/draaidagen/upcoming';
const TTL = 5 * MINUUT;

/**
 * De oude route filterde op startdatum ≥ startDate; een meerdaagse draaidag die vóór het
 * venster begint en erin doorloopt viel dan weg. Twee weken ruimer vragen (voor de nieuwe
 * route, die op overlap filtert, alleen wat extra; het venster knipt draaidagTakenPerPersoon).
 */
const RUIMTE_DAGEN = 14;
const ruimVanaf = (vanaf) => {
  const d = new Date(`${String(vanaf).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return vanaf;
  d.setDate(d.getDate() - RUIMTE_DAGEN);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const eerste = (v) => (Array.isArray(v) ? v[0] : v);
const lijst = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const puurDatum = (v) => {
  const s = String(v || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};
export const looksLikeAirtableId = (v) => /^rec[A-Za-z0-9]{14}$/.test(String(v || '').trim());

/** Gaat deze draaidag door? (4-laags filter; ontbrekende velden laten hem staan) */
export function isActieveDraaidag(f = {}) {
  if (f.is_valid === false || f._is_valid === false) return false;
  if (f.all_day === false) return false;
  if (f.hidden === true || f.Verberg === true) return false;
  if (f.outlook_link_status === false || f.OutlookLinkStatus === false) return false;
  return true;
}

/** "OPTIE(2 dagen): Mick + Pepijn || Keukenkampioen - [9760] - LJ" → optie? */
export const isOptieNaam = (naam) => /\boptie\b/i.test(String(naam || ''));

/**
 * Draaidagtitel zonder crew en opties: het deel ná "||" (of ná de eerste "|"),
 * zonder het staartje " - [nummer] - PL".
 */
export function kaleDraaidagNaam(naam) {
  let s = String(naam || '').trim();
  if (!s) return '';
  const delen = s.split(/\s*\|\|\s*|\s+\|\s+/);
  if (delen.length > 1) s = delen.slice(1).join(' ');
  else s = s.replace(/^(optie|studio)\b\s*(\([^)]*\))?\s*:?\s*/i, '');
  s = s.replace(/\s*-\s*\[[^\]]*\]\s*(-\s*[A-Za-z]{1,4})?\s*$/, '').replace(/\s+-\s*$/, '').trim();
  return s;
}

/**
 * Ruwe records (/Api/draaidagen) → genormaliseerde draaidagen. Crew uit de records zelf
 * (Email (from Crew), Crew, CrewLeden); `aankomend` (/Api/draaidagen/upcoming) vult alleen
 * aan voor een oude engine zonder die velden.
 */
export function normaliseerDraaidagen(records, aankomend = []) {
  const emailsPerId = new Map();
  (aankomend || []).forEach((a) => {
    if (a?.id) emailsPerId.set(a.id, { emails: lijst(a.crewEmail), ids: lijst(a.crew) });
  });
  return (records || []).map((r) => {
    const f = r?.fields || {};
    if (!r?.id || !isActieveDraaidag(f)) return null;
    const start = puurDatum(f.StartDatum);
    if (!start) return null;
    const eind = puurDatum(f.EindDatum) || start;
    const titel = String(f.Draaidag || f.Naam || '').trim();
    const projectnaam = String(eerste(f['Projectnaam (from Project)']) || eerste(f.Projectnaam) || '').trim();
    const extra = emailsPerId.get(r.id) || { emails: [], ids: [] };
    const leden = lijst(f.CrewLeden).filter((l) => l && typeof l === 'object');
    const emails = [...extra.emails, ...lijst(f['Email (from Crew)']), ...leden.map((l) => l.email)]
      .map((e) => String(e || '').trim().toLowerCase()).filter(Boolean);
    const ids = [...extra.ids, ...lijst(f.Crew), ...leden.map((l) => l.id)].filter(looksLikeAirtableId);
    const naam = projectnaam || kaleDraaidagNaam(titel) || 'Draaidag';
    return {
      id: r.id,
      titel,
      naam,
      optie: isOptieNaam(titel),
      start,
      eind: eind < start ? start : eind,
      projectnummer: String(eerste(f['Projectnummer (from Project)']) || '').trim(),
      crewNamen: lijst(f['Naam (from Crew)']).map((n) => String(n || '').trim()).filter(Boolean),
      crewEmails: [...new Set(emails)],
      crewIds: [...new Set(ids)],
    };
  }).filter(Boolean);
}

const voornaamVan = (p) => String(p?.firstName || String(p?.name || '').split(' ')[0] || '').trim().toLowerCase();

/**
 * Welke personen (id's) staan op deze draaidag? E-mail eerst, dan record-id, en een
 * voornaam alleen als hij eenduidig is (bij twee Brams: alleen als er precies één
 * medewerker Bram is; anders niemand — liever niets dan de verkeerde).
 */
export function crewPersoonIds(draaidag, persons) {
  const uit = new Set();
  const perEmail = new Map();
  const perId = new Map();
  (persons || []).forEach((p) => {
    const e = String(p?.email || '').trim().toLowerCase();
    if (e && !perEmail.has(e)) perEmail.set(e, []);
    if (e) perEmail.get(e).push(p);
    if (p?.id) perId.set(p.id, p);
  });
  const gekoppeldeVoornamen = new Set();
  (draaidag.crewEmails || []).forEach((e) => {
    const kandidaten = perEmail.get(e) || [];
    // Gedeelde postbus (freelance@…): meerdere personen, dus geen koppeling op e-mail.
    if (kandidaten.length === 1) { uit.add(kandidaten[0].id); gekoppeldeVoornamen.add(voornaamVan(kandidaten[0])); }
  });
  const namenAlsId = (draaidag.crewNamen || []).filter(looksLikeAirtableId);
  [...(draaidag.crewIds || []), ...namenAlsId].forEach((id) => {
    const p = perId.get(id);
    if (p) { uit.add(p.id); gekoppeldeVoornamen.add(voornaamVan(p)); }
  });
  (draaidag.crewNamen || []).filter((n) => !looksLikeAirtableId(n)).forEach((naam) => {
    const vn = String(naam).trim().split(/\s+/)[0].toLowerCase();
    if (!vn || gekoppeldeVoornamen.has(vn)) return;
    const volledig = String(naam).trim().toLowerCase();
    let kandidaten = (persons || []).filter((p) => String(p?.name || '').trim().toLowerCase() === volledig);
    if (!kandidaten.length) kandidaten = (persons || []).filter((p) => voornaamVan(p) === vn);
    if (kandidaten.length > 1) kandidaten = kandidaten.filter((p) => String(p?.type || '') === 'Medewerker');
    if (kandidaten.length === 1) { uit.add(kandidaten[0].id); gekoppeldeVoornamen.add(vn); }
  });
  return [...uit];
}

const isEchteDraaidagTaak = (t) => !t?.bronDraaidag && (t?.type === 'Draaidag' || (t?.prio || t?.status) === 'Draaidag');
const kaal = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Kalenderdagen van `van` t/m `tot` (YYYY-MM-DD), in UTC gerekend (geen zomertijdschuif). */
function dagenTussen(van, tot) {
  const uit = [];
  const d = new Date(`${van}T00:00:00Z`);
  const eind = new Date(`${tot}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(eind.getTime())) return uit;
  for (let i = 0; d <= eind && i < 400; i += 1) {
    uit.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return uit;
}

/**
 * Is deze echte Draaidag-taak dezelfde draaidag als `d` (uit de bron)? Alleen dan mag hij
 * de bron-draaidag vervangen. Op record-id (als de taak ernaar linkt), projectnummer, of naam
 * (projectnaam/titel). Geen herkenning = een andere shoot: dan blijven ze allebei staan.
 */
export function isZelfdeDraaidag(taak, d) {
  if (!taak || !d) return false;
  const links = [taak.draaidagId, ...lijst(taak.draaidag), ...lijst(taak.draaidagen), ...lijst(taak.Draaidag), ...lijst(taak.Draaidagen)]
    .map((v) => String(v || '').trim()).filter(Boolean);
  if (d.id && links.includes(d.id)) return true;
  const nr = String(d.projectnummer || '').trim();
  const taakNr = String(taak.projectNumber || taak.projectnummer || '').trim();
  if (nr && taakNr) return nr === taakNr;
  const naam = kaal(taak.name || taak.naam);
  if (!naam) return false;
  const bron = [kaal(d.naam), kaal(d.titel), kaal(kaleDraaidagNaam(d.titel))].filter((n) => n.length >= 4 && n !== 'draaidag');
  if (bron.some((n) => naam === n || naam.includes(n) || (naam.length >= 6 && n.includes(naam)))) return true;
  return !!nr && new RegExp(`\\[\\s*${nr}\\s*\\]`).test(String(taak.name || ''));
}

/** Aaneengesloten reeksen uit een gesorteerde lijst dagen: [[van, tot], …]. */
function reeksen(dagen) {
  const uit = [];
  dagen.forEach((dag) => {
    const laatste = uit[uit.length - 1];
    if (laatste && dagenTussen(laatste[1], dag).length === 2) laatste[1] = dag;
    else uit.push([dag, dag]);
  });
  return uit;
}

/**
 * Draaidagen als taken per persoon-id, in de vorm die de capaciteitsberekening al kent
 * (Type 'Draaidag' = volle dag; naam die met 'Optie' begint = optie, gestreept).
 * Heeft iemand al een echte Draaidag-taak voor **dezelfde** draaidag, dan telt die — maar
 * alleen voor de dagen die de taak dekt: een taak van 4 u op maandag haalt maandag weg,
 * niet de hele draaidag ma–wo. Een taak voor een andere shoot haalt niets weg.
 * Het label staat in de naam, niet in project-velden: de uitklap in Capaciteit toont dan
 * precies "Draaidag · <projectnaam>".
 */
export function draaidagTakenPerPersoon(draaidagen, persons, bestaandeTaken = {}, { van, tot } = {}) {
  const uit = {};
  (draaidagen || []).forEach((d) => {
    if (van && d.eind < van) return;
    if (tot && d.start > tot) return;
    crewPersoonIds(d, persons).forEach((pid) => {
      const gedekt = new Set();
      (bestaandeTaken?.[pid] || []).filter(isEchteDraaidagTaak).forEach((t) => {
        const s = puurDatum(t.startDate);
        if (!s || !isZelfdeDraaidag(t, d)) return;
        const e = puurDatum(t.endDate) || s;
        dagenTussen(s, e < s ? s : e).forEach((dag) => gedekt.add(dag));
      });
      const over = dagenTussen(d.start, d.eind).filter((dag) => !gedekt.has(dag));
      if (!over.length) return;
      const delen = gedekt.size ? reeksen(over) : [[d.start, d.eind]];
      delen.forEach(([s, e]) => {
        (uit[pid] = uit[pid] || []).push({
          id: delen.length > 1 || s !== d.start || e !== d.eind ? `draaidag:${d.id}:${s}` : `draaidag:${d.id}`,
          name: `${d.optie ? 'Optie' : 'Draaidag'} · ${d.naam}`,
          type: 'Draaidag',
          status: 'Draaidag',
          startDate: s,
          endDate: e,
          bronDraaidag: true,
          draaidagId: d.id,
          draaidagTitel: d.titel,
          draaidagCrew: d.crewNamen,
          draaidagOptie: d.optie,
          draaidagProjectnummer: d.projectnummer,
        });
      });
    });
  });
  return uit;
}

/** Is dit agenda-item dezelfde draaidag (zelfde onderwerp, titel of projectnummer)? */
export function isZelfdeAfspraak(onderwerp, taak) {
  const o = kaal(onderwerp);
  if (!o) return false;
  const titel = kaal(taak?.draaidagTitel);
  if (titel && (o === titel || o.includes(titel) || titel.includes(o))) return true;
  const kern = kaal(kaleDraaidagNaam(taak?.draaidagTitel));
  if (kern.length >= 6 && o.includes(kern)) return true;
  const nr = String(taak?.draaidagProjectnummer || '').trim();
  return !!nr && new RegExp(`\\[\\s*${nr}\\s*\\]`).test(String(onderwerp || ''));
}

const minuten = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

// Werkdagvenster waarbinnen een afspraak als bezetting telt — gelijk aan de engine
// (CAPACITEIT_VENSTER_UUR 8–18, privé 9–17 in mail_assistant_routes.py).
const WERKDAG_MIN = [8 * 60, 18 * 60];
const PRIVE_MIN = [9 * 60, 17 * 60];

/**
 * Bezette uren van een dag uit losse afspraken, met overlap één keer geteld — zoals de
 * engine (_bezette_uren). Elk tijdvak wordt eerst op het werkdagvenster geknipt (zoals de
 * engine doet), dan samengevoegd: 07–10 + 08–10 = 2 uur, niet 3. Afspraken die over
 * middernacht lopen of geen tijden hebben tellen met hun eigen `hours` (de engine knipt
 * die niet). Nooit meer dan de som van de eigen uren.
 */
export function bezetteUren(afspraken) {
  const vakken = [];
  let zonderTijd = 0;
  let som = 0;
  (afspraken || []).forEach((ev) => {
    const h = Number(ev?.hours) || 0;
    som += h;
    const s = minuten(ev?.start_time);
    const e = minuten(ev?.end_time);
    if (s === null || e === null || e <= s) { zonderTijd += h; return; }
    const [v0, v1] = ev?.prive ? PRIVE_MIN : WERKDAG_MIN;
    const ks = Math.max(s, v0);
    const ke = Math.min(e, v1);
    if (ke > ks) vakken.push([ks, ke]);
  });
  vakken.sort((a, b) => a[0] - b[0]);
  let totaal = 0;
  let van = null;
  let tot = null;
  vakken.forEach(([s, e]) => {
    if (van === null) { van = s; tot = e; return; }
    if (s <= tot) { tot = Math.max(tot, e); return; }
    totaal += tot - van; van = s; tot = e;
  });
  if (van !== null) totaal += tot - van;
  return Math.min(totaal / 60 + zonderTijd, som);
}

/**
 * Agenda (engine-vorm { hours, events, … }) zonder de afspraken die de draaidag zélf zijn.
 * Een shoot staat vaak ook als getimede afspraak in Outlook; die uren telden in Capaciteit
 * als "extra werk bovenop de draaidag", waardoor een draaidag als rood "100%" verscheen en
 * het woord Draaidag wegviel (Joris, 26 sep: "je wilt hier draaidagen laten terugkomen").
 * Alleen afspraken op een dag van die draaidag én met hetzelfde onderwerp/projectnummer
 * (isZelfdeAfspraak) gaan eruit; andere meetings op die dag blijven gewoon meetellen.
 * De uren van zo'n dag worden opnieuw berekend uit de overgebleven afspraken (overlap één
 * keer), niet afgetrokken: shoot 09–17 met een meeting 12–13 erin is 1 uur, geen 0.
 * @param draaidagTaken { persoonId: [draaidag-taken] } — alle draaidagen van de periode
 *   (zie alleDraaidagTaken), niet alleen de aangevulde.
 */
export function agendaZonderDraaidag(agenda, persons, draaidagTaken) {
  if (!agenda?.events || !draaidagTaken) return agenda;
  let gewijzigd = false;
  const hours = { ...(agenda.hours || {}) };
  const events = { ...agenda.events };
  (persons || []).forEach((p) => {
    const taken = draaidagTaken[p?.id];
    const email = String(p?.email || '').trim().toLowerCase();
    if (!taken?.length || !email || !Array.isArray(events[email])) return;
    const lijst = events[email];
    const weg = new Set(lijst.filter((ev) => {
      const dag = String(ev?.date || '').slice(0, 10);
      return taken.some((t) => {
        const s = puurDatum(t.startDate);
        const e = puurDatum(t.endDate) || s;
        return s && dag >= s && dag <= e && isZelfdeAfspraak(ev?.subject, t);
      });
    }));
    if (!weg.size) return;
    gewijzigd = true;
    const rest = lijst.filter((ev) => !weg.has(ev));
    events[email] = rest;
    const uren = { ...(hours[email] || {}) };
    new Set([...weg].map((ev) => String(ev.date).slice(0, 10))).forEach((dag) => {
      const nieuw = bezetteUren(rest.filter((ev) => String(ev?.date || '').slice(0, 10) === dag));
      const oud = Number(uren[dag]) || 0;
      const waarde = Math.min(oud, Math.round(nieuw * 100) / 100); // nooit meer dan de engine telde
      if (waarde > 0.005) uren[dag] = waarde; else delete uren[dag];
    });
    hours[email] = uren;
  });
  return gewijzigd ? { ...agenda, hours, events } : agenda;
}

/**
 * Alle draaidagen van de periode per persoon, voor agendaZonderDraaidag: die uit de
 * draaidagenbron (ook als er al een Draaidag-taak voor bestaat) én de echte Draaidag-taken
 * (herkend op hun naam en projectnummer). Anders telde de Outlook-shoot van een al
 * ingeplande draaidag nog dubbel.
 */
export function alleDraaidagTaken(draaidagen, persons, takenPerPersoon = {}, venster = {}) {
  const uit = draaidagTakenPerPersoon(draaidagen, persons, {}, venster);
  Object.entries(takenPerPersoon || {}).forEach(([pid, lijst]) => {
    (lijst || []).filter(isEchteDraaidagTaak).forEach((t) => {
      (uit[pid] = uit[pid] || []).push({
        ...t,
        draaidagTitel: t.draaidagTitel || t.name || t.naam || '',
        draaidagProjectnummer: t.draaidagProjectnummer || t.projectNumber || t.projectnummer || '',
      });
    });
  });
  return uit;
}

/** Levert de engine de crew al mee (nieuwe route)? Dan is /upcoming niet nodig. */
export function heeftCrewVelden(records) {
  return (records || []).some((r) => {
    const f = r?.fields || {};
    return Array.isArray(f.CrewLeden) || Array.isArray(f['Email (from Crew)']);
  });
}

const vensterArgs = (vanaf, tot) => {
  const startDate = ruimVanaf(vanaf);
  const endDate = puurDatum(tot);
  return endDate && endDate >= String(startDate).slice(0, 10) ? { startDate, endDate } : { startDate };
};
const aankomendSleutel = () => planningSleutel(EP_AANKOMEND, { weeks: 12 });

/** Wat er al in de cache ligt (voor een directe beginstand), of null. */
export function draaidagenUitCache(vanaf, tot) {
  const ruw = leesCache(planningSleutel(EP_DRAAIDAGEN, vensterArgs(vanaf, tot)), { maxLeeftijd: TTL });
  if (!ruw) return null;
  if (!ruw.waarde?.length || heeftCrewVelden(ruw.waarde)) return normaliseerDraaidagen(ruw.waarde);
  const extra = leesCache(aankomendSleutel(), { maxLeeftijd: TTL });
  return normaliseerDraaidagen(ruw.waarde, extra?.waarde || []);
}

/**
 * Alle actieve draaidagen die [vanaf, tot] raken (YYYY-MM-DD), genormaliseerd. Via de
 * gedeelde planningcache (5 min), zodat Rooster en Capaciteit één keer ophalen.
 * Levert de engine de crew-velden niet (oude engine), dan komen de e-mails fail-soft uit
 * /upcoming; zonder die valt de koppeling terug op de voornaam.
 */
export async function haalDraaidagen(vanaf, { tot, forceer = false } = {}) {
  const args = vensterArgs(vanaf, tot);
  const query = new URLSearchParams(args).toString();
  const records = await haal(planningSleutel(EP_DRAAIDAGEN, args), async () => {
    const r = await authFetch(`${BACKEND_URL}${EP_DRAAIDAGEN}?${query}`);
    const d = await leesJson(r, 'Draaidagen laden');
    return Array.isArray(d) ? d : (d?.draaidagen || []);
  }, { maxLeeftijd: TTL, forceer });
  if (!records?.length || heeftCrewVelden(records)) return normaliseerDraaidagen(records);
  const extra = await haal(aankomendSleutel(), async () => {
    const r = await authFetch(`${BACKEND_URL}${EP_AANKOMEND}?weeks=12`);
    const d = await leesJson(r, 'Aankomende draaidagen laden');
    return d?.draaidagen || [];
  }, { maxLeeftijd: TTL, forceer }).catch(() => []);
  return normaliseerDraaidagen(records, extra);
}
